import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import SettingsSystemDaydreamIcon from "@mui/icons-material/SettingsSystemDaydream";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { formatDateTime } from "../../utils/formatDate";
import { viewPayload, StatusBadge } from "./DocumentsList";
import { DocumentPreview } from "./AddDocument";

const ViewDocument = () => {
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { document_key, document_id } = location.state || {};

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    if (document_key || document_id) {
      fetchDocumentDetails();
    } else {
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document_key, document_id]);

  const fetchDocumentDetails = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("/documents/view", viewPayload({ document_key, document_id }));
      if (res?.data?.status === "success") {
        setViewData(res.data.data);
      } else {
        showSnackbar("error", res?.data?.message || "Failed to fetch document details");
      }
    } catch (e) {
      if (e?.response?.status !== 401) {
        showSnackbar("error", e?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setOverAllLoading(false);
    }
  };

  if (!viewData && !overAllLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No document data found.
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const uploadedBy = [viewData?.uploaded_by?.first_name, viewData?.uploaded_by?.last_name].filter(Boolean).join(" ");
  const codeName = (code, name) => [code, name].filter(Boolean).join(" - ") || "-";

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading && <OverAllLoader message="Fetching document details..." />}
      {viewData && (
        <Box>
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>Back</Typography>
          </Grid>

          <Box>
            <Grid sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #d1d1d1" }}>
              <Grid sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
                <Grid sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px" }}>
                  <FolderOpenOutlinedIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>Document Details</Typography>
              </Grid>
            </Grid>

            <Box sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}>
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Basic Information</Typography>
              </Grid>

              <Grid sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Document Name</Typography>
                    <Typography sx={{ overflowWrap: "anywhere" }}>{viewData.document_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusBadge status={viewData.status} />
                    </Box>
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">{getLabel("PROGRAMS")}</Typography>
                    <Typography>{codeName(viewData.program_code, viewData.program_name)}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">{getLabel("SUBJECT")}</Typography>
                    <Typography>{codeName(viewData.category_code, viewData.category_name)}</Typography>
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">File Name</Typography>
                    <Typography sx={{ overflowWrap: "anywhere" }}>{viewData.file_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">File Size</Typography>
                    <Typography>{viewData.file_size_mb != null ? `${viewData.file_size_mb} MB` : "-"}</Typography>
                  </Grid>
                </Grid>
                {viewData.failure_reason && (
                  <Grid>
                    <Typography fontWeight="600">Failure Reason</Typography>
                    <Typography sx={{ color: "#d32f2f", overflowWrap: "anywhere" }}>{viewData.failure_reason}</Typography>
                  </Grid>
                )}
              </Grid>

              {/* File card — click to open the PDF in the viewer */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PictureAsPdfOutlinedIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Document</Typography>
              </Grid>

              <DocumentPreview document={viewData} />

              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SettingsSystemDaydreamIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>System Information</Typography>
              </Grid>

              <Grid sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Uploaded By</Typography>
                    <Typography>{uploadedBy || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Uploaded Date</Typography>
                    <Typography>{formatDateTime(viewData.uploaded_at) || "-"}</Typography>
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Last Updated</Typography>
                    <Typography>{formatDateTime(viewData.updated_at) || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ViewDocument;
